import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiBriefcase, FiLogOut } from 'react-icons/fi';
import { logout } from '../../store/slices/authSlice';
import { isDelegated } from '../../services/api';
import { formatDate } from '../../utils/dateFormat';

/**
 * Shown for the whole of a delegation (Vekalet) session. The session identity
 * is the delegator, so `user` here is the person whose workspace we are in and
 * `user.delegation` is who is really at the keyboard.
 *
 * Reuses the impersonation banner's markup and styling with a --delegation
 * modifier: the two are the same kind of "you are not yourself right now"
 * warning, and telling them apart by colour is cheaper than a second layout.
 */
function DelegationBanner() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);

  if (!user?.delegation || !isDelegated()) return null;

  const exit = () => {
    dispatch(logout());
    navigate('/login');
  };

  const { delegation } = user;
  const delegatorName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
  const initials = `${(user.firstName || '')[0] || ''}${(user.lastName || '')[0] || ''}`.toUpperCase();

  const scopeLabels = [
    delegation.scopes?.canInitiateContract && t('delegation.scopes.initiate'),
    delegation.scopes?.canAdvanceProcess && t('delegation.scopes.advance'),
  ].filter(Boolean);

  return (
    <div className="impersonation-banner impersonation-banner--delegation" role="alert">
      <div className="impersonation-banner__info">
        <span className="impersonation-banner__badge" aria-hidden="true">
          <FiBriefcase />
          <span className="impersonation-banner__pulse" />
        </span>
        <span className="impersonation-banner__avatar" aria-hidden="true">{initials}</span>
        <span className="impersonation-banner__texts">
          <span className="impersonation-banner__title">
            {t('delegation.bannerTitle', { delegator: delegatorName })}
          </span>
          <span className="impersonation-banner__subtitle">
            {t('delegation.bannerDetail', {
              scopes: scopeLabels.join(' · '),
              until: formatDate(delegation.endDate),
            })}
          </span>
        </span>
      </div>
      <button type="button" className="impersonation-banner__exit" onClick={exit}>
        <FiLogOut /> {t('delegation.exit')}
      </button>
    </div>
  );
}

export default DelegationBanner;
